import { Link } from "@inertiajs/react"
import { LegalPage } from "@/components/LegalPage"

type Props = { last_updated: string; contact_email: string }

const title = "Terms of Use"
const description =
  "The terms that apply when you use the Australian Music Database — accounts, contributions, copyright and takedown requests, donations, and accuracy."

export default function Terms({ last_updated, contact_email }: Props) {
  return (
    <LegalPage title={title} description={description} lastUpdated={last_updated}>
      <section>
        <p>
          These terms apply when you use australianmusicdatabase.com. By browsing the site or creating an
          account, you accept them. The site is run by Mark Gibson as a research archive of Australian
          music history.
        </p>
      </section>

      <section>
        <h2>Accuracy of the database</h2>
        <p>
          The database is a continuous research project, built up over many years from many sources. It
          is offered in good faith, but it is <strong>not guaranteed to be complete or correct</strong>.
          Dates, line-ups, chart positions and gig details are frequently revised as better sources come
          to light.
        </p>
        <p>
          Please do not treat it as an authoritative record for legal, commercial or biographical
          purposes without verifying independently. If you spot an error, tell us — corrections are
          genuinely welcome and are how the archive improves.
        </p>
      </section>

      <section>
        <h2>Your account</h2>
        <p>
          You are responsible for keeping your password secure and for what happens under your account.
          Don't share it, and don't create an account using someone else's identity. We may suspend or
          remove accounts used to vandalise records, submit deliberately false information, or harass
          others.
        </p>
      </section>

      <section>
        <h2>Contributions</h2>
        <p>
          If you add or edit content, you confirm that you have the right to submit it and that doing so
          does not infringe anyone else's copyright. You grant us permission to publish, store and edit
          your contribution as part of the database, and to keep it there if you later close your
          account — the factual record needs to remain intact.
        </p>
        <p>
          Contributions are publicly attributed to your account. Attribution can be removed on request;
          see <Link href="/data-deletion">Data Deletion</Link>.
        </p>
      </section>

      <section>
        <h2>Copyright and takedown</h2>
        <p>
          The database contains historical material — photographs, scans, gig listings and chart data —
          much of it decades old and contributed by many people over many years. It is compiled for
          historical and research purposes.
        </p>
        <p>
          <strong>If you hold rights to something published here and want it removed, contact us and it
          will be taken down.</strong> Please include the specific page and what you hold rights to.
          We would rather remove a disputed item promptly than argue about it. Email{" "}
          <a href={`mailto:${contact_email}`}>{contact_email}</a>.
        </p>
        <p>
          The database's own compiled text and structure remain the site's copyright. You are welcome to
          read, link to and cite it; please don't bulk-scrape it or republish it wholesale as your own.
        </p>
      </section>

      <section>
        <h2>Donations</h2>
        <p>
          Donations are voluntary one-time payments that help cover hosting and running costs. Depending
          on the amount, a donation grants ad-free access for a period or for life, as described on the{" "}
          <Link href="/donate">Donate</Link> page.
        </p>
        <p>
          A donation is a contribution to the project, not the purchase of a product, and is not
          tax-deductible. Ad-free access is a thank-you, not a service guarantee. If something goes wrong
          with a payment, contact us and we will sort it out.
        </p>
      </section>

      <section>
        <h2>Availability</h2>
        <p>
          The site is provided as-is, without warranty of any kind. It may be unavailable at times for
          maintenance or reasons outside our control, and features may change or be withdrawn. To the
          extent permitted by law, we are not liable for any loss arising from your use of the site or
          reliance on its contents. Nothing here excludes rights you have under Australian Consumer Law
          that cannot be excluded.
        </p>
      </section>

      <section>
        <h2>Changes</h2>
        <p>
          These terms may change. Material changes will be reflected in the "last updated" date above,
          and continuing to use the site means accepting the current version.
        </p>
      </section>

      <section>
        <h2>Governing law</h2>
        <p>These terms are governed by the laws of Australia.</p>
      </section>

      <section>
        <h2>Contact</h2>
        <p>
          Questions about these terms:{" "}
          <a href={`mailto:${contact_email}`}>{contact_email}</a>. See also our{" "}
          <Link href="/privacy">Privacy Policy</Link>.
        </p>
      </section>
    </LegalPage>
  )
}
